import { useEffect } from "react";

/**
 * Lightweight head/SEO manager. No external library — manipulates
 * document.head directly, with cleanup to avoid leaking tags between routes.
 *
 * Usage:
 *   <Seo title="..." description="..." canonical="..." jsonLd={[{...}, {...}]} ogImage="..." />
 */
export default function Seo({
  title,
  description,
  canonical,
  jsonLd,
  ogImage,
  lang,
  ogType = "website",
  noindex = false,
  alternates,
}) {
  useEffect(() => {
    const cleanups = [];

    if (title) {
      const prev = document.title;
      document.title = title;
      cleanups.push(() => {
        document.title = prev;
      });
    }

    if (lang && document.documentElement.lang !== lang) {
      const prev = document.documentElement.lang;
      document.documentElement.lang = lang;
      cleanups.push(() => {
        document.documentElement.lang = prev;
      });
    }

    const setMeta = (key, content, isProp = false) => {
      if (!content) return;
      const selector = isProp ? `meta[property="${key}"]` : `meta[name="${key}"]`;
      let el = document.head.querySelector(selector);
      let created = false;
      if (!el) {
        el = document.createElement("meta");
        if (isProp) el.setAttribute("property", key);
        else el.setAttribute("name", key);
        document.head.appendChild(el);
        created = true;
      }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      cleanups.push(() => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute("content", prev);
      });
    };

    setMeta("description", description);
    // Force noindex on any non-production host (preview, staging, etc.) so
    // search engines never index those environments regardless of the
    // per-route `noindex` prop.
    const forceNoindex = noindex || !isProductionHost();
    setMeta(
      "robots",
      forceNoindex ? "noindex,nofollow" : "index,follow,max-image-preview:large"
    );
    setMeta("og:title", title, true);
    setMeta("og:description", description, true);
    setMeta("og:type", ogType, true);
    // Dynamic og:locale based on the page's language so EN pages advertise
    // themselves as en_GB in social embeds (was previously hardcoded to
    // es_ES on every page).
    setMeta("og:locale", lang === "en" ? "en_GB" : "es_ES", true);
    if (canonical) setMeta("og:url", canonical, true);
    if (ogImage) setMeta("og:image", ogImage, true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    if (ogImage) setMeta("twitter:image", ogImage);

    // Canonical
    if (canonical) {
      let linkEl = document.head.querySelector('link[rel="canonical"]');
      let created = false;
      const prev = linkEl ? linkEl.getAttribute("href") : null;
      if (!linkEl) {
        linkEl = document.createElement("link");
        linkEl.setAttribute("rel", "canonical");
        document.head.appendChild(linkEl);
        created = true;
      }
      linkEl.setAttribute("href", canonical);
      cleanups.push(() => {
        if (created) linkEl.remove();
        else if (prev !== null) linkEl.setAttribute("href", prev);
      });
    }

    // hreflang alternates. `alternates` is { es, en, "x-default" } (any subset).
    // Each entry renders a <link rel="alternate" hreflang="…" href="…">.
    // We tag them with data-seo-alt so cleanup can safely purge only ours.
    const altLinks = [];
    if (alternates && typeof alternates === "object") {
      for (const [hreflang, href] of Object.entries(alternates)) {
        if (!hreflang || !href) continue;
        const link = document.createElement("link");
        link.setAttribute("rel", "alternate");
        link.setAttribute("hreflang", hreflang);
        link.setAttribute("href", href);
        link.dataset.seoAlt = "csm";
        document.head.appendChild(link);
        altLinks.push(link);
      }
    }
    cleanups.push(() => altLinks.forEach((l) => l.remove()));

    // JSON-LD blocks
    const lds = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    const scripts = [];
    for (const data of lds) {
      if (!data) continue;
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seo = "csm";
      try {
        script.text = JSON.stringify(data);
      } catch {
        continue;
      }
      document.head.appendChild(script);
      scripts.push(script);
    }
    cleanups.push(() => scripts.forEach((s) => s.remove()));

    return () => cleanups.forEach((fn) => fn && fn());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, ogImage, lang, ogType, noindex, JSON.stringify(jsonLd || null), JSON.stringify(alternates || null)]);

  return null;
}

/* ------- Helpers to build common structured-data blocks ------- */
// Canonical site URL. Reads from REACT_APP_SITE_URL at build time (baked into
// the bundle) and falls back to the production domain. Never falls back to a
// preview URL — canonicals/OG must always point at the indexable production
// site regardless of which environment is serving the bundle.
const SITE_URL =
  (typeof process !== "undefined" &&
    process.env &&
    process.env.REACT_APP_SITE_URL) ||
  "https://cortijodesantamaria.com";

// Runtime helper — used by the render effect below to force noindex on any
// non-production host so the preview env never appears in search results
// even if REACT_APP_SITE_URL was misconfigured.
export const PRODUCTION_HOST = "cortijodesantamaria.com";
export const isProductionHost = () => {
  if (typeof window === "undefined") return true;
  return window.location.hostname === PRODUCTION_HOST;
};

export const getSiteUrl = () => SITE_URL;

export function organizationLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Cortijo Santa María",
    url: SITE_URL,
    logo: `${SITE_URL}/logo192.png`,
    email: "info@cortijodesantamaria.com",
    telephone: "+34626411029",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Limones, Moclín",
      postalCode: "18247",
      addressRegion: "Granada",
      addressCountry: "ES",
    },
    sameAs: [
      "https://www.instagram.com/cortijo.santamaria/",
      "https://share.google/DHVU40DKgz3p7UbUO",
    ],
  };
}

export function websiteLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Cortijo Santa María",
    url: SITE_URL,
  };
}

export function productLd(product, url, lang = "es", includeOffer = true) {
  if (!product) return null;
  const name = lang === "es" ? product.name_es : product.name_en;
  const description = lang === "es" ? product.short_desc_es : product.short_desc_en;
  const base = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description,
    image: [product.image_url, ...(product.gallery || [])],
    sku: product.slug,
    brand: { "@type": "Brand", name: "Cortijo Santa María" },
    category: lang === "es" ? "Aceite de oliva virgen extra" : "Extra virgin olive oil",
  };
  if (!includeOffer) return base;
  return {
    ...base,
    offers: {
      "@type": "Offer",
      url,
      priceCurrency: product.currency || "EUR",
      price: (product.price || 0).toFixed(2),
      availability:
        product.stock > 0
          ? "https://schema.org/InStock"
          : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "Cortijo Santa María" },
    },
  };
}

export function faqLd(items) {
  if (!items || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((i) => ({
      "@type": "Question",
      name: i.q,
      acceptedAnswer: { "@type": "Answer", text: i.a },
    })),
  };
}

export function articleLd(post, url, lang = "es") {
  if (!post) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.hero_image ? [post.hero_image] : undefined,
    datePublished: post.date,
    dateModified: post.date,
    inLanguage: lang === "es" ? "es-ES" : "en-GB",
    author: { "@type": "Organization", name: "Cortijo Santa María" },
    publisher: {
      "@type": "Organization",
      name: "Cortijo Santa María",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/logo192.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };
}

export function breadcrumbLd(items) {
  if (!items || items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: it.url,
    })),
  };
}
